import { OpenFundingOpportunities } from "./OpenFundingOpportunities";
import { useCurrentUser } from "@client/hooks/use-current-user";
import { useImpersonationRefValue } from "@client/ImperativeState";
import { RouteScroller } from "@client/components/utils/RouteScroller";
import { Link } from "wouter";
import { Page } from "@client/components/utils/Page";
import { LoginPanel } from "@lib/auth/client/Login/LoginButton";
import { trpc } from "@client/trpc";
import { Button, Skeleton } from "@sfc/ui";
import { ApplicationLoading } from "./ApplicationLoading";
import { FormGuideView } from "@client/pages/application-builder/FormGuideView";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "server";

type Application =
  inferRouterOutputs<AppRouter>["getPublicOpenApplications"][number];
export function OpenApplicationList({
  applications,
  layout = "list",
  showHeading = true,
}: {
  applications: Application[];
  layout?: "list" | "cards" | "directory";
  showHeading?: boolean;
}) {
  if (applications.length === 0) return null;
  return (
    <section
      aria-labelledby={showHeading ? "open-applications-heading" : undefined}
      className="w-full space-y-4"
    >
      {showHeading && (
        <div>
          <h2 id="open-applications-heading" className="heading-lg">
            Apply for Funding
          </h2>
          <p className="mt-2 body-md text-muted-foreground">
            Explore funding rounds for your team’s work.
          </p>
        </div>
      )}
      <ul
        className={
          layout === "directory"
            ? "grid min-w-0 gap-4"
            : layout === "cards"
              ? "grid min-w-0 gap-4 sm:grid-cols-2"
              : "min-w-0 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card"
        }
      >
        {applications.map((application) => (
          <li
            key={application.roundId}
            className={
              layout !== "list"
                ? "overflow-hidden rounded-lg border border-border bg-card"
                : undefined
            }
          >
            <Link
              href={`/apply/${encodeURIComponent(application.roundId)}`}
              className={
                layout === "directory"
                  ? "group flex h-full w-full flex-col items-start gap-6 p-6 text-left text-foreground transition-colors hover:bg-surface-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring sm:flex-row sm:items-center sm:justify-between md:p-8"
                  : "group flex h-full w-full flex-wrap items-start justify-start text-left gap-4 px-6 py-5 text-foreground transition-colors hover:bg-muted/40 hover:text-primary focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
              }
            >
              {layout === "directory" ? (
                <span className="min-w-0 space-y-2">
                  <span className="block body-sm font-medium text-primary">
                    Accepting Applications
                  </span>
                  <span className="block heading-lg [overflow-wrap:anywhere]">
                    {application.roundName}
                  </span>
                </span>
              ) : (
                <span className="heading-sm w-full min-w-0 text-left [overflow-wrap:anywhere]">
                  {application.roundName}
                </span>
              )}
              <span
                className={
                  layout !== "list"
                    ? "shrink-0 rounded-md bg-primary px-4 py-3 label-md text-primary-foreground"
                    : "label-md shrink-0 text-primary group-hover:underline underline-offset-4"
                }
              >
                View Application →
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
type Preview = inferRouterOutputs<AppRouter>["getPublicApplicationPreview"];
type PrivatePreview = inferRouterOutputs<AppRouter>["getApplicationPreview"];

export function ApplicationPreview({
  application,
  viewerId,
}: {
  application: Preview | PrivatePreview;
  viewerId?: string;
}) {
  const data =
    !viewerId ||
    ("viewerId" in application && application.viewerId === viewerId)
      ? application
      : undefined;
  if (!data) return <ApplicationPreviewLoadingContent />;
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-6 border-b border-border pb-8">
        <div className="max-w-2xl space-y-3">
          <h1 className="application-title">{data.roundName}</h1>
          <p className="body-lg text-muted-foreground">
            Preview the questions before you apply.
          </p>
        </div>
        {(!viewerId || ("canApply" in data && data.canApply === true)) && (
          <Button
            nativeButton={false}
            render={
              <Link
                href={`/apply/${encodeURIComponent(data.roundId)}?apply=1`}
              />
            }
          >
            Apply
          </Button>
        )}
      </header>
      <FormGuideView content={data.content} applicant />
    </div>
  );
}
export function PublicOpenApplications() {
  const query = trpc.getPublicOpenApplications.useQuery(undefined, {
    retry: false,
    gcTime: 0,
  });
  if (query.isError)
    return (
      <div role="alert" className="space-y-3">
        <p>Couldn’t load open funding rounds.</p>
        <Button variant="outline" onClick={() => void query.refetch()}>
          Try again
        </Button>
      </div>
    );
  if (!query.isSuccess || query.data.length === 0) return null;
  return (
    <div className="w-full mt-10">
      <OpenFundingOpportunities applications={query.data} />
    </div>
  );
}

export function PublicRoundApplication({
  roundId,
  signingIn,
}: {
  roundId: string;
  signingIn: boolean;
}) {
  const query = trpc.getPublicApplicationPreview.useQuery(
    { roundId },
    {
      retry: false,
      gcTime: 0,
    },
  );
  const application = query.data;
  if (query.isPending && !signingIn) return <ApplicationPreviewLoading />;
  if (application && !signingIn)
    return <ApplicationPreviewPage application={application} />;
  return (
    <Page className="mx-auto max-w-5xl space-y-6">
      <span hidden data-application-theme="green" />
      {query.isPending ? (
        <ApplicationLoading />
      ) : query.isError ? (
        <p role="alert">
          This application is unavailable, or you may need to sign in to access
          it.
        </p>
      ) : !application ? (
        <p>This round is not currently accepting applications.</p>
      ) : (
        <>
          <h1 className="application-title">{application.title}</h1>
        </>
      )}
      {(signingIn || query.isError) && (
        <section className="max-w-md space-y-6">
          <h2 className="heading-lg">Sign in to continue</h2>
          <LoginPanel />
        </section>
      )}
    </Page>
  );
}

export function ApplicationPreviewPage({
  application,
  viewerId,
}: {
  application: Preview | PrivatePreview;
  viewerId?: string;
}) {
  return (
    <ApplicationPreviewShell>
      <ApplicationPreview application={application} viewerId={viewerId} />
    </ApplicationPreviewShell>
  );
}

function ApplicationPreviewShell({ children }: { children: React.ReactNode }) {
  return (
    <RouteScroller>
      <main className="mx-auto w-full max-w-[1200px] space-y-8 px-6 py-8 md:px-10">
        <span hidden data-application-theme="green" />
        <Link
          href="/apply"
          className="inline-block label-md text-primary hover:underline underline-offset-4"
        >
          ← Funding Rounds
        </Link>
        {children}
      </main>
    </RouteScroller>
  );
}

export function ApplicationPreviewLoading() {
  return (
    <ApplicationPreviewShell>
      <ApplicationPreviewLoadingContent />
    </ApplicationPreviewShell>
  );
}

function ApplicationPreviewLoadingContent() {
  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-6 border-b border-border pb-8">
        <div className="w-full max-w-2xl space-y-3">
          <Skeleton
            className="h-9 w-3/4 motion-reduce:animate-none"
            aria-hidden="true"
          />
          <p className="body-lg text-muted-foreground">
            Preview the questions before you apply.
          </p>
        </div>
      </header>
      <div className="grid items-start gap-8 lg:grid-cols-[14rem_minmax(0,1fr)]">
        <div className="space-y-3" aria-hidden="true">
          <Skeleton className="h-6 w-3/4 motion-reduce:animate-none" />
          <Skeleton className="h-6 w-2/3 motion-reduce:animate-none" />
        </div>
        <ApplicationLoading layout="questions" />
      </div>
    </div>
  );
}

export function SignedInFundingPage() {
  const { data: user } = useCurrentUser();
  const impersonation = useImpersonationRefValue();
  return (
    <FundingDirectory authenticated viewerId={impersonation ?? user?.userId} />
  );
}

export function FundingDirectory({
  authenticated = false,
  viewerId,
}: {
  authenticated?: boolean;
  viewerId?: string;
}) {
  const publicQuery = trpc.getPublicOpenApplications.useQuery(undefined, {
    enabled: !authenticated,
    retry: false,
    gcTime: 0,
  });
  const privateQuery = trpc.getOpenApplications.useQuery(undefined, {
    enabled: authenticated && !!viewerId,
    retry: false,
    gcTime: 0,
  });
  const query = authenticated ? privateQuery : publicQuery;
  const applications = authenticated
    ? privateQuery.data?.viewerId === viewerId
      ? privateQuery.data?.applications
      : undefined
    : publicQuery.data;
  return (
    <RouteScroller>
      <main className="mx-auto w-full max-w-[1120px] space-y-8 px-4 py-8 md:px-8 md:py-10">
        <span hidden data-application-theme="green" />
        <Link
          href="/"
          className="body-md text-primary hover:underline underline-offset-4"
        >
          ← Home
        </Link>
        <header className="grid items-start gap-8 border-b border-border pb-8 md:grid-cols-[minmax(0,1fr)_18rem] md:gap-16 md:pb-10">
          <div className="space-y-4">
            <h1 className="application-title">Apply for Funding</h1>
            <p className="max-w-xl body-lg text-muted-foreground">
              Find a funding round for your team’s work.
            </p>
            <p className="max-w-xl body-md text-muted-foreground">
              Read through the questions before you start. When you’re ready,
              choose a team and work on your application together.
            </p>
          </div>
          <div className="space-y-3 border-l border-border pl-6">
            <h2 className="heading-sm">Before You Apply</h2>
            <p className="body-md text-muted-foreground">
              You can preview every application without signing in.
            </p>
            <p className="body-md text-muted-foreground">
              Team members can edit and submit the same application.
            </p>
          </div>
        </header>
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="heading-md">Open Funding Rounds</h2>
          {applications && (
            <span className="body-sm text-muted-foreground">
              {applications.length}{" "}
              {applications.length === 1 ? "round" : "rounds"}
            </span>
          )}
        </div>
        {query.isError ? (
          <div role="alert" className="space-y-3">
            <p>Couldn’t load open funding rounds.</p>
            <Button variant="nav" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        ) : applications === undefined ? (
          <ApplicationLoading
            label="Loading funding rounds"
            rows={2}
            layout="directory"
          />
        ) : applications.length === 0 ? (
          <p className="body-md text-muted-foreground">
            There are no open funding rounds right now. Please check back later.
          </p>
        ) : (
          <OpenApplicationList
            applications={applications}
            layout="directory"
            showHeading={false}
          />
        )}
      </main>
    </RouteScroller>
  );
}
